import React from 'react';
import { Card } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Progress } from '../../../../foundation/altzero_base/base/components/ui/progress';
import { CheckCircle, AlertCircle, AlertTriangle, Loader2, Clock, FileText, RefreshCw, X } from 'lucide-react';
import { ParsedOffer } from '../../contexts/BatchParsingContext';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';

interface BatchParsingProgressProps {
    parsedOffers: ParsedOffer[];
    overallProgress: number;
    isParsing: boolean;
    onRetry: (offerId: string) => void;
    onCancelOffer: (offerId: string) => void;
    onCancelAll: () => void;
}

export const BatchParsingProgress: React.FC<BatchParsingProgressProps> = ({
    parsedOffers,
    overallProgress,
    isParsing,
    onRetry,
    onCancelOffer,
    onCancelAll,
}) => {
    const completedCount = parsedOffers.filter(o => o.status === 'completed').length;
    const errorCount = parsedOffers.filter(o => o.status === 'error').length;
    const cancelledCount = parsedOffers.filter(o => o.status === 'cancelled').length;
    const totalCount = parsedOffers.length;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
            case 'reviewed':
                return 'text-green-600 bg-green-50';
            case 'error':
                return 'text-red-600 bg-red-50';
            case 'parsing':
                return 'text-blue-600 bg-blue-50';
            case 'cancelled':
                return 'text-orange-600 bg-orange-50';
            case 'queued':
                return 'text-gray-600 bg-gray-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
            case 'reviewed':
                return <CheckCircle className="w-4 h-4" />;
            case 'error':
                return <AlertCircle className="w-4 h-4" />;
            case 'parsing':
                return <Loader2 className="w-4 h-4 animate-spin" />;
            case 'cancelled':
                return <X className="w-4 h-4" />;
            case 'queued':
                return <Clock className="w-4 h-4" />;
            default:
                return <FileText className="w-4 h-4" />;
        }
    };

    return (
        <Card className="p-6 space-y-6">
            {/* Overall Progress Header */}
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">
                        Parsing Progress: {completedCount + errorCount + cancelledCount} of {totalCount}
                    </h3>
                    <div className="flex items-center gap-2">
                        {isParsing && (
                            <span className="flex items-center gap-2 text-sm text-blue-600">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing...
                            </span>
                        )}
                        {isParsing && (
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={onCancelAll}
                                className="flex items-center gap-1"
                            >
                                <X className="w-3 h-3" />
                                Cancel All
                            </Button>
                        )}
                    </div>
                </div>

                <div className="relative">
                    <Progress
                        value={overallProgress}
                        className="h-3"
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                        {Math.round(overallProgress)}%
                    </div>
                </div>

                {/* Statistics */}
                <div className="flex gap-6 text-sm">
                    <div className="flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4  text-green-600" />
                        <span className="font-medium">{completedCount}</span>
                        <span className="text-gray-600">Completed</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span className="font-medium">{errorCount}</span>
                        <span className="text-gray-600">Failed</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Loader2 className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">{parsedOffers.filter(o => o.status === 'parsing').length}</span>
                        <span className="text-gray-600">In Progress</span>
                    </div>
                    {cancelledCount > 0 && (
                        <div className="flex items-center gap-1.5">
                            <X className="w-4 h-4 text-orange-600" />
                            <span className="font-medium">{cancelledCount}</span>
                            <span className="text-gray-600">Cancelled</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Individual Offer Cards */}
            <div className="space-y-2">
                {parsedOffers.map((offer) => (
                    <div
                        key={offer.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Status Icon */}
                            <div className={`p-2 rounded-full ${getStatusColor(offer.status)}`}>
                                {getStatusIcon(offer.status)}
                            </div>

                            {/* File Info */}
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{offer.fileName}</p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="capitalize">{offer.status}</span>
                                    {offer.status === 'completed' && offer.warnings && offer.warnings.length > 0 && (
                                        <>
                                            <span>•</span>
                                            <span className="text-amber-600 font-medium">
                                                {offer.warnings.length} to check
                                            </span>
                                        </>
                                    )}
                                    {offer.error && (
                                        <>
                                            <span>•</span>
                                            <span className="text-red-600 truncate">{offer.error}</span>
                                        </>
                                    )}
                                </div>

                                {/* A parse can succeed and still be wrong: a charge the retailer
                                    states on its other offers but this document did not yield is
                                    money missing from the offer, and null looks identical to "not
                                    charged". Spelled out in full rather than behind a tooltip —
                                    acting on it means opening the document and typing a number. */}
                                {offer.status === 'completed' && offer.warnings && offer.warnings.length > 0 && (
                                    <ul className="mt-1.5 space-y-1">
                                        {offer.warnings.map((w, i) => (
                                            <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                                                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                                                <span>{w}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Progress Bar */}
                            {offer.status === 'parsing' && (
                                <div className="w-32">
                                    <Progress value={offer.progress} className="h-2" />
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                            {offer.status === 'error' && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onRetry(offer.id)}
                                    className="flex items-center gap-1"
                                >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                </Button>
                            )}

                            {(offer.status === 'queued' || offer.status === 'parsing') && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onCancelOffer(offer.id)}
                                    className="flex items-center gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                                >
                                    <X className="w-3 h-3" />
                                    Cancel
                                </Button>
                            )}

                            {offer.status === 'completed' && (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};
